import { Alert, Box } from '@mui/material';

import {
  CustomButton,
  CustomTextArea,
  CustomTextField,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
  LoadingSkeleton,
} from '../../../common/components';
import { drawerFieldSpan as span } from '../../../common/styles';
import { formatDateTime } from '../../../utils/formatUtils';
import { materialCell, materialMoney } from '../../../utils/materialDisplay';
import { VALUATION_TEXTS as TEXTS } from '../constants/materialValuationConstants';
import { formatOpeningEvidence } from '../utils/formatOpeningEvidence';
import { formatOpeningUsd } from '../utils/formatOpeningUsd';

const DRAWER_WIDTH = { xs: '100vw', md: 'min(720px, calc(100vw - 40px))' };

/**
 * Detalle de solo lectura de la última apertura aprobada: valores, cotización, respaldo y
 * aprobador.
 * @param {Object} props
 * @param {boolean} props.open Visibilidad del drawer.
 * @param {Object|undefined} props.opening Apertura devuelta por el backend.
 * @param {boolean} props.isLoading Consulta en curso.
 * @param {string} props.error Mensaje de error de carga.
 * @param {Function} props.onClose Cierra el drawer.
 * @returns {JSX.Element}
 */
const MaterialOpeningDetailDrawer = ({ open, opening, isLoading, error, onClose }) => (
  <DrawerForm
    open={open}
    onClose={onClose}
    title={TEXTS.detailTitle}
    width={DRAWER_WIDTH}
    footer={(
      <DrawerFormActions
        summary={(
          <DrawerFormActionSummary
            label={TEXTS.totalValueArs}
            value={opening ? materialMoney(opening.total_value_ars) : '-'}
          />
        )}
      >
        <CustomButton variant="outlined" onClick={onClose}>
          {TEXTS.close}
        </CustomButton>
      </DrawerFormActions>
    )}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
      {error && (
        <Box sx={{ mx: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {isLoading && <LoadingSkeleton />}

      {opening && (
        <>
          <DrawerFormSection title={TEXTS.detailStockSection}>
            <CustomTextField
              label={TEXTS.article}
              value={`${opening.article_code} · ${opening.article_name}`}
              disabled
              sx={span(12)}
            />
            <CustomTextField
              label={TEXTS.warehouse}
              value={opening.warehouse_name || '-'}
              disabled
              sx={span(6)}
            />
            <CustomTextField
              label={TEXTS.approvedQuantity}
              value={materialCell('quantity', opening.quantity)}
              disabled
              sx={span(3)}
            />
            <CustomTextField
              label={TEXTS.remainingQuantity}
              value={materialCell('quantity', opening.remaining_quantity)}
              disabled
              sx={span(3)}
            />
          </DrawerFormSection>

          <DrawerFormSection title={TEXTS.detailValuesSection}>
            <CustomTextField
              label={TEXTS.unitCostArs}
              value={materialMoney(opening.unit_cost_ars)}
              disabled
              sx={span(6)}
            />
            <CustomTextField
              label={TEXTS.totalValueArs}
              value={materialMoney(opening.total_value_ars)}
              disabled
              sx={span(6)}
            />
            <CustomTextField
              label={TEXTS.unitCostUsd}
              value={formatOpeningUsd(opening.unit_cost_usd)}
              disabled
              sx={span(6)}
            />
            <CustomTextField
              label={TEXTS.totalValueUsd}
              value={formatOpeningUsd(opening.total_value_usd)}
              disabled
              sx={span(6)}
            />
            <CustomTextField
              label={TEXTS.quoteRate}
              value={`${materialMoney(opening.quote_ars_per_usd)} (${opening.quote_market})`}
              disabled
              sx={span(6)}
            />
            <CustomTextField
              label={TEXTS.quoteAsOf}
              value={formatDateTime(opening.quote_as_of)}
              disabled
              sx={span(6)}
            />
          </DrawerFormSection>

          <DrawerFormSection title={TEXTS.detailSourceSection}>
            <CustomTextField
              label={TEXTS.estimated}
              value={opening.is_estimated ? TEXTS.estimatedValue : TEXTS.documentedValue}
              disabled
              sx={span(12)}
            />
            <CustomTextArea
              label={TEXTS.notes}
              value={opening.source_notes || ''}
              disabled
              sx={span(12)}
            />
            <CustomTextArea
              label={TEXTS.evidence}
              value={formatOpeningEvidence(opening.evidence)}
              disabled
              sx={span(12)}
            />
          </DrawerFormSection>

          <DrawerFormSection title={TEXTS.detailApprovalSection}>
            <CustomTextField
              label={TEXTS.approvedBy}
              value={opening.approved_by_name || '-'}
              disabled
              sx={span(6)}
            />
            <CustomTextField
              label={TEXTS.approvedAt}
              value={formatDateTime(opening.approved_at)}
              disabled
              sx={span(6)}
            />
          </DrawerFormSection>
        </>
      )}
    </Box>
  </DrawerForm>
);

export default MaterialOpeningDetailDrawer;
